'use client';

/* eslint-disable react/jsx-props-no-spreading */
import { c as _c } from "react/compiler-runtime";
import { DEFAULT_COUNTRY } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { useCombobox } from 'downshift';
import noop from 'lodash/noop';
import take from 'lodash/take';
import type React from 'react';
import { forwardRef, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import type { InputProps } from '../internal/input';
import Input from '../internal/input';
import type { AddressPrediction } from './address-input.types';
import AddressDropdownFooter from './components/address-dropdown-footer';
import AddressDropdownItem from './components/address-dropdown-item';
import useAddressPredictions from './hooks/use-address-predictions';
import usePopper from './hooks/use-popper';
const MAX_OF_RESULTS = 5;
type AddressInputProps = Omit<InputProps, 'onSelect'> & {
  onSelect?: (prediction?: AddressPrediction | null, details?: {
    selectedOnBlur: boolean;
  }) => void;
  country?: Iso3166TwoDigitCountryCode;
};
const AddressInput = forwardRef<HTMLInputElement, AddressInputProps>((t0, ref) => {
  const $ = _c(17);
  let onChangeText;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let value;
  if ($[0] !== t0) {
    ({
      country: t1,
      onBlur: t2,
      onChange: t3,
      onChangeText,
      onKeyDown: t4,
      onSelect: t5,
      value,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onChangeText;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = value;
  } else {
    onChangeText = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    value = $[8];
  }
  const country = t1 === undefined ? DEFAULT_COUNTRY.value : t1;
  const onBlur = t2 === undefined ? noop : t2;
  const onChange = t3 === undefined ? noop : t3;
  const onKeyDown = t4 === undefined ? noop : t4;
  const onSelect = t5 === undefined ? noop : t5;
  const isUncontrolled = value === undefined;
  const localRef = useRef(null);
  const {
    setReferenceElement,
    setPopperElement,
    popper
  } = usePopper();
  const {
    predictions,
    search,
    clear
  } = useAddressPredictions(country);
  const options = take(predictions, MAX_OF_RESULTS);
  let t7;
  if ($[9] !== onChangeText || $[10] !== search) {
    t7 = t8 => {
      const {
        inputValue: t9,
        type: type_0
      } = t8;
      const nextInputValue = t9 === undefined ? "" : t9;
      onChangeText?.(nextInputValue);
      if (type_0 === useCombobox.stateChangeTypes.InputChange) {
        search(nextInputValue);
      }
    };
    $[9] = onChangeText;
    $[10] = search;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen: downShiftIsOpen
  } = useCombobox({
    items: options,
    itemToString: _temp,
    onSelectedItemChange: t6 => {
      const {
        selectedItem,
        type
      } = t6;
      onChangeText?.(selectedItem?.mainText?.text ?? selectedItem?.text.text ?? "");
      clear();
      onSelect(selectedItem, {
        selectedOnBlur: type === useCombobox.stateChangeTypes.InputBlur
      });
      const hasToTriggerChangeEvent = isUncontrolled && localRef.current;
      if (hasToTriggerChangeEvent && localRef.current) {
        const event = new window.Event("change", {
          bubbles: true
        });
        localRef.current.dispatchEvent(event);
      }
    },
    onInputValueChange: t7
  });
  let t8;
  if ($[12] !== getComboboxProps) {
    t8 = getComboboxProps();
    $[12] = getComboboxProps;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const comboBoxProps = t8;
  const toggleButtonProps = getToggleButtonProps();
  const inputProps = getInputProps();
  const menuProps = getMenuProps({}, {
    suppressRefError: true
  });
  const hasResults = options.length > 0;
  const isDropdownOpen = downShiftIsOpen && hasResults;
  const handleChange = event_0 => {
    inputProps.onChange(event_0);
    onChange(event_0);
  };
  const handleBlur = event_1 => {
    inputProps.onBlur(event_1);
    onBlur(event_1);
  };
  const handleKeyDown = event_2 => {
    inputProps.onKeyDown(event_2);
    onKeyDown(event_2);
  };
  let t9;
  if ($[14] !== getItemProps || $[15] !== highlightedIndex) {
    t9 = (item_0, index) => {
      const itemProps = getItemProps({
        item: item_0,
        index
      });
      return <AddressDropdownItem data-dd-privacy="mask" disableHoverStyles={highlightedIndex !== -1} highlighted={highlightedIndex === index} key={item_0.placeId} subtitle={item_0.secondaryText?.text ?? ""} title={item_0.mainText?.text ?? item_0.text.text} {...itemProps} />;
    };
    $[14] = getItemProps;
    $[15] = highlightedIndex;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const renderAddressItem = t9;
  return <div {...comboBoxProps}><div ref={setReferenceElement} className="relative"><Input {...props} {...toggleButtonProps} aria-autocomplete={inputProps["aria-autocomplete"]} aria-controls={inputProps["aria-controls"]} autoComplete="address-line1" autoCapitalize="words" autoCorrect="off" spellCheck={false} id={props.id || inputProps.id} onBlur={handleBlur} onChange={handleChange} onKeyDown={handleKeyDown} tabIndex={0} value={value} data-dd-privacy="mask" ref={mergeRefs([localRef, inputProps.ref, toggleButtonProps.ref, ref])} />{isDropdownOpen ? <ul className="fp-dropdown-menu max-h-[332px] w-full outline-none pt-2" {...menuProps} {...popper.attributes.popper} ref={mergeRefs([menuProps.ref, setPopperElement])} style={popper.styles.popper} data-dd-privacy="mask">{options.map(renderAddressItem)}<AddressDropdownFooter /></ul> : null}</div></div>;
});
export default AddressInput;
export type { AddressInputProps };
function _temp(item) {
  return item ? item.mainText?.text ?? item.text.text : "";
}